import { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChatWidget = ({ booking, user, userType }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (open && booking) {
      fetchMessages();
      const interval = setInterval(fetchMessages, 3000);
      return () => clearInterval(interval);
    }
  }, [open, booking]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/chat/messages/${booking.id}`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setLoading(true);
    try {
      await axios.post(`${API}/chat/messages`, {
        booking_id: booking.id,
        sender_id: user.id || user.email,
        sender_name: user.name || user.full_name,
        sender_type: userType,
        message: newMessage
      });
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const handleCall = () => {
    const phone = userType === 'customer' ? booking.barber_phone : booking.customer_phone;
    if (phone) {
      window.location.href = `tel:${phone}`;
    } else {
      toast.error('Phone number not available');
    }
  };

  if (!booking) return null;

  return (
    <>
      {/* Floating Chat Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-[#00C167] hover:bg-[#009E54] text-white rounded-full shadow-lg flex items-center justify-center z-40 transition-transform hover:scale-110"
        >
          <MessageCircle className="w-6 h-6" />
          {messages.length > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
              {messages.length}
            </span>
          )}
        </button>
      )}

      {/* Chat Window */}
      {open && (
        <div className="fixed bottom-6 right-6 w-96 h-[500px] bg-[var(--bg-secondary)] dark:bg-[#1A1A1A] border border-[var(--border-color)] rounded-xl shadow-2xl z-40 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-[var(--border-color)] bg-[#00C167] rounded-t-xl">
            <div className="flex items-center gap-3">
              <MessageCircle className="w-5 h-5 text-white" />
              <div>
                <div className="font-semibold text-white text-sm">
                  {userType === 'customer' ? booking.barber_name : booking.customer_name}
                </div>
                <div className="text-xs text-white/80">{booking.service_name}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleCall}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                title="Call"
              >
                <Phone className="w-4 h-4 text-white" />
              </button>
              <button onClick={() => setOpen(false)} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 ? (
              <div className="text-center text-[var(--text-secondary)] text-sm mt-8">
                No messages yet. Start the conversation!
              </div>
            ) : (
              messages.map((msg, idx) => {
                const isMe = msg.sender_type === userType;
                return (
                  <div key={idx} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-lg p-3 ${isMe ? 'bg-[#00C167] text-white' : 'bg-[var(--bg-primary)] dark:bg-[#0A0A0A] text-[var(--text-primary)]'}`}>
                      {!isMe && <div className="text-xs opacity-70 mb-1">{msg.sender_name}</div>}
                      <div className="text-sm">{msg.message}</div>
                      <div className={`text-xs mt-1 ${isMe ? 'text-white/70' : 'text-[var(--text-secondary)]'}`}>
                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={sendMessage} className="p-4 border-t border-[var(--border-color)]">
            <div className="flex gap-2">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a message..."
                className="flex-1"
                disabled={loading}
              />
              <Button type="submit" disabled={loading || !newMessage.trim()} className="bg-[#00C167] hover:bg-[#009E54]">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatWidget;